import { useCallback, useMemo, useState } from 'react';
import { useModal } from '@lucid/lego';
import { useStore } from '../../../../../store/store';
import type { Prospect } from '../../../../../api/services/lupp/prospect/dto.prospect';
import type { ProspectTestDrive } from '../../../../../api/services/lupp/prospect/dto.test-drive-schedule';
import { useCreateCheckInAgreement } from '../../../../../api/services/lupp/prospect/prospect';
import {
  isTestDriveCheckInEnabled,
  isTestDriveCheckInAlwaysVisible,
} from '../../../../../config/featureFlags';

export const CHECK_IN_DOCUMENT_LOCALES = [
  'en_us',
  'en_ca',
  'fr_ca',
  'fr_ch',
  'de_ch',
  'no_no',
  'nl_nl',
  'de_de',
  'ar_sa',
  'en_nl',
  'en_de',
  'en_no',
  'en_fr',
  'en_ch',
];

export const DOCUMENT_LOCALE_OPTIONS = CHECK_IN_DOCUMENT_LOCALES.map((documentLocale) => ({
  label: documentLocale.replace('_', '-'),
  value: documentLocale,
}));

export const getDocumentLocale = (value?: string) => {
  const normalized = value?.toLowerCase().replace('-', '_');
  return normalized && CHECK_IN_DOCUMENT_LOCALES.includes(normalized) ? normalized : undefined;
};

export const getProspectDocumentLocale = (prospect?: Prospect) => {
  const countryCode = prospect?.countryCode?.toLowerCase();
  const language = prospect?.preferredLanguageIso?.toLowerCase();
  const candidate = countryCode && language ? `${language}_${countryCode}` : undefined;
  return (
    getDocumentLocale(candidate) ?? getDocumentLocale(countryCode ? `en_${countryCode}` : undefined)
  );
};

type UseTestDriveCheckInParams = {
  prospect?: Prospect;
  testDrive?: ProspectTestDrive;
  labels?: Record<string, string>;
  onError?: (message: string) => void;
};

/**
 * Shared check-in logic reused by the test drive "details" (view) and
 * "modify" (edit) surfaces. `canCheckIn` requires the check-in flag to be on,
 * a Scheduled test drive with an SFDC identifier, and the scheduled date to be
 * today in the test drive's own timezone, unless development visibility testing is enabled.
 */
export const useTestDriveCheckIn = ({
  prospect,
  testDrive,
  labels,
  onError,
}: UseTestDriveCheckInParams) => {
  const locale = useStore((state) => state.locale);
  const { mutateAsync: createCheckInAgreement, isPending: isCreatingCheckInAgreement } =
    useCreateCheckInAgreement();

  const docusignModal = useModal();
  const [docusignUrl, setDocusignUrl] = useState<string | null>(null);
  const [selectedCheckInLocale, setSelectedCheckInLocale] = useState<string | undefined>();

  const resolvedCheckInLocale = useMemo(
    () =>
      selectedCheckInLocale ??
      getDocumentLocale(locale) ??
      getProspectDocumentLocale(prospect) ??
      'en_us',
    [selectedCheckInLocale, locale, prospect]
  );

  const canCheckIn = useMemo(() => {
    if (!isTestDriveCheckInEnabled()) {
      return false;
    }
    if (!prospect?.prospectIdentifier || !testDrive?.testDriveScheduleId) {
      return false;
    }
    if (isTestDriveCheckInAlwaysVisible()) {
      return true;
    }
    if (testDrive.testDriveStatus?.toLowerCase() !== 'scheduled') {
      return false;
    }
    if (!testDrive.sfdcSalesEventId || !testDrive.scheduledStartDatetime) {
      return false;
    }
    try {
      const timezone = testDrive.timezone || 'UTC';
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      return (
        formatter.format(new Date(testDrive.scheduledStartDatetime)) ===
        formatter.format(new Date())
      );
    } catch {
      return false;
    }
  }, [prospect?.prospectIdentifier, testDrive]);

  const handleCheckIn = useCallback(
    async (customLocale?: string) => {
      const localeToUse = customLocale || resolvedCheckInLocale;
      if (!prospect?.prospectIdentifier || !testDrive?.testDriveScheduleId || !localeToUse) {
        return;
      }
      try {
        const response = await createCheckInAgreement({
          prospectId: prospect.prospectIdentifier,
          testDriveId: testDrive.testDriveScheduleId,
          data: { preferredLocale: localeToUse },
        });
        if (!response.viewUrl) {
          throw new Error('No document URL returned.');
        }
        setDocusignUrl(response.viewUrl);
        docusignModal.openModal();
      } catch (error) {
        onError?.(
          error instanceof Error
            ? error.message
            : (labels?.['details.checkInError'] ?? 'Failed to start check-in. Please try again.')
        );
      }
    },
    [
      prospect?.prospectIdentifier,
      testDrive?.testDriveScheduleId,
      resolvedCheckInLocale,
      createCheckInAgreement,
      docusignModal,
      labels,
      onError,
    ]
  );

  const handleCloseDocusign = useCallback(() => {
    docusignModal.closeModal();
    setDocusignUrl(null);
  }, [docusignModal]);

  const handleDocusignMessage = useCallback(
    (event: MessageEvent) => {
      const data = typeof event.data === 'string' ? event.data : event.data?.event;
      if (data === 'signing_complete') handleCloseDocusign();
    },
    [handleCloseDocusign]
  );

  return {
    canCheckIn,
    resolvedCheckInLocale,
    setSelectedCheckInLocale,
    handleCheckIn,
    isCreatingCheckInAgreement,
    docusignUrl,
    docusignModal,
    handleCloseDocusign,
    handleDocusignMessage,
  };
};
